import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { customApi, fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ButtonCst from "@components/ButtonCst";
import DownloadIcon from "@mui/icons-material/Download";
import {
  ModalGroupEnrollmentCstProps,
  StaticGroup,
  StaticGroupsResponse,
} from "./types";

const ModalGroupEnrollmentCst: React.FC<ModalGroupEnrollmentCstProps> = ({
  open,
  onClose,
  corporateId,
  objectId,
  editionId,
  onSuccess,
  onCompleteDisclaimer,
}) => {
  const { t } = useTranslation(["modal", "common"]);
  const theme = useTheme();
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [status, setStatus] = useState<{
    s: StatusEnum | undefined;
    message: string;
  }>({ s: undefined, message: "" });
  const [responseData, setResponseData] = useState<any>(null);
  const [isGroupApplyCompleted, setIsGroupApplyCompleted] =
    useState<boolean>(false);

  // Fetch static groups
  const { data: groupsData, isLoading: isLoadingGroups } =
    useSWR<StaticGroupsResponse>(
      open ? `${ServicesURL.getStaticGroups}?corporateId=${corporateId}` : null,
      (url: string) => fetcher<StaticGroupsResponse>(url, MethodType.GET),
      {
        revalidateOnFocus: true,
        revalidateOnMount: true,
      },
    );

  // POST mutation for group enrollment
  const { trigger: triggerUpload } = useSWRMutation(
    `${ServicesURL.postGroupEnrollmentUpload}`,
    (
      url: string,
      {
        arg,
      }: {
        arg: {
          objectId: string;
          editionId: string;
          corporateId: string;
          groupId: string;
        };
      },
    ) =>
      customApi<any>(
        `${url}?objectId=${arg.objectId}&editionId=${arg.editionId}&corporateId=${arg.corporateId}&groupId=${arg.groupId}`,
        MethodType.POST,
      ),
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedGroupId("");
      setStatus({ s: undefined, message: "" });
      setResponseData(null);
    }
  }, [open]);

  const handleApply = async () => {
    if (!selectedGroupId) return;

    setStatus({ s: StatusEnum.LOADING, message: "" });
    setResponseData(null);

    try {
      const response: any = await triggerUpload({
        objectId,
        editionId,
        corporateId,
        groupId: selectedGroupId,
      });

      // Check if response contains rejection data (CSV with discards)
      if (response?.headers?.["content-disposition"]?.includes("filename")) {
        setResponseData(response);
        setStatus({
          s: StatusEnum.OK,
          message:
            t("upload-with-discards", { ns: "modal" }) ||
            "Caricamento completato con scarti",
        });
      } else {
        setStatus({
          s: StatusEnum.OK,
          message:
            t("upload-success", { ns: "modal" }) ||
            "Caricamento completato con successo",
        });
        onSuccess?.();
      }
      setIsGroupApplyCompleted(true);
    } catch (error: any) {
      console.error("Error uploading group enrollment:", error);
      const errorMessage =
        error?.response?.data?.errors?.FATAL?.[0]?.message ||
        error?.response?.data?.errors?.UNKNOWN?.[0]?.message ||
        error?.response?.data?.errorMessage ||
        t("upload-error", { ns: "modal" }) ||
        "Errore durante il caricamento";
      setStatus({ s: StatusEnum.KO, message: errorMessage });
    }
  };

  const handleDownloadDiscards = () => {
    if (!responseData) return;

    const type = responseData.headers?.["content-type"] || "text/csv";
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, responseData.data], { type });
    const downloadUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.href = downloadUrl;
    link.download =
      responseData.headers?.["content-disposition"]?.split("filename=")?.[1] ||
      "scarti.csv";
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  const getStatusColor = () => {
    switch (status.s) {
      case StatusEnum.KO:
        return theme.palette.error.main;
      default:
        return theme.palette.text.primary;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="group-enrollment-modal-title"
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.6)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          border: `2px solid ${theme.palette.divider}`,
          pt: 2,
          px: 4,
          pb: 3,
          width: "95vw",
          minHeight: "240px",
          [theme.breakpoints.up("md")]: {
            maxWidth: "700px",
            minWidth: "600px",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            id="group-enrollment-modal-title"
            variant="h6"
            component="h2"
            sx={{ margin: 0 }}
          >
            {t("group-enrollment-title", { ns: "modal" }) ||
              "Carica iscrizioni"}
          </Typography>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: "50%",
              minWidth: "0",
              padding: "0 3px",
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              span: {
                width: "0",
              },
            }}
          >
            <CloseIcon fontSize="large" />
          </Button>
        </Box>

        <Typography
          sx={{
            mt: 2,
            fontStyle: "italic",
            color: "text.primary",
          }}
        >
          {t("disclaimer", { ns: "modal" })}
        </Typography>

        {/* Content - Select and Apply button on same row */}
        <Box
          sx={{
            mt: 4,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {isLoadingGroups ? (
            <Box sx={{ display: "flex", justifyContent: "center", flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="group-select-label">
                  {t("groups", { ns: "modal" })}
                </InputLabel>
                <Select
                  labelId="group-select-label"
                  id="group-select"
                  value={selectedGroupId}
                  label={t("groups", { ns: "modal" })}
                  onChange={(e) => {
                    setSelectedGroupId(e.target.value);
                    setStatus({ s: undefined, message: "" });
                    setResponseData(null);
                    setIsGroupApplyCompleted(false);
                  }}
                  disabled={status.s === StatusEnum.LOADING}
                >
                  {groupsData?.groups?.map((group: StaticGroup) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                id="apply-group-enrollment"
                variant="contained"
                color="primary"
                onClick={handleApply}
                disabled={!selectedGroupId || status.s === StatusEnum.LOADING}
                sx={{ minWidth: "100px", height: "40px" }}
              >
                {status.s === StatusEnum.LOADING ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("apply", { ns: "modal" })
                )}
              </Button>
            </>
          )}
        </Box>

        {/* Status message with download button */}
        {status.s && status.s !== StatusEnum.LOADING && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ color: getStatusColor(), flex: 1 }}>
              {status.message}
            </Typography>
            {responseData &&
              responseData?.headers?.["content-disposition"]?.includes(
                "filename",
              ) && (
                <ButtonCst
                  id="download-discards"
                  variant="outlined"
                  size="small"
                  onClick={handleDownloadDiscards}
                  sx={{ ml: 2 }}
                >
                  <DownloadIcon />
                </ButtonCst>
              )}
          </Box>
        )}

        {onCompleteDisclaimer && isGroupApplyCompleted && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: "rgba(46, 125, 50, 0.1)",
              border: "1px solid",
              borderColor: "success.main",
            }}
          >
            <Typography
              sx={{
                color: "success.main",
                fontSize: "0.875rem",
              }}
            >
              {t("on-complete-disclaimer", { ns: "modal" })}
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default ModalGroupEnrollmentCst;
